import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Users, TrendingUp, Clock, RefreshCw, Wifi } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import { useNavigate } from 'react-router-dom';

const statusStyles = {
  pending: 'bg-orange-100 text-orange-700',
  confirmed: 'bg-blue-100 text-blue-700',
  ready: 'bg-green-100 text-green-700',
  delivered: 'bg-gray-100 text-gray-600',
};

const statusEmoji = {
  pending: '🟡',
  confirmed: '🔵',
  ready: '🟢',
  delivered: '✅',
};

export default function Dashboard() {
  const [restaurant, setRestaurant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    ordersToday: 0,
    revenueToday: 0,
    totalCustomers: 0,
    pendingOrders: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: rest } = await supabase
        .from('restaurants')
        .select('*')
        .eq('email', user.email)
        .single();

      if (!rest) return;
      setRestaurant(rest);

      const { data: allOrders } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', rest.id)
        .order('created_at', { ascending: false });

      const ordersData = allOrders || [];
      setOrders(ordersData.slice(0, 5));

      const todayStart = new Date();
      todayStart.setUTCHours(0, 0, 0, 0);

      const todayOrders = ordersData.filter(o => new Date(o.created_at) >= todayStart);
      const revenueToday = todayOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
      const pendingOrders = ordersData.filter(o => o.status === 'pending').length;

      const { count: customerCount } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true })
        .eq('restaurant_id', rest.id);

      setStats({
        ordersToday: todayOrders.length,
        revenueToday,
        totalCustomers: customerCount || 0,
        pendingOrders,
      });

      setLastUpdated(new Date());

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Real-time subscription
 useEffect(() => {
  let channel = null;
  let mounted = true;

  const setupRealtime = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !mounted) return;

      const { data: rest } = await supabase
        .from('restaurants')
        .select('id')
        .eq('email', user.email)
        .single();

      if (!rest || !mounted) return;

      channel = supabase.channel(`dash-${rest.id}`);
      
      channel.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'orders',
        filter: `restaurant_id=eq.${rest.id}`
      }, () => {
        if (mounted) fetchData(true);
      });

      await channel.subscribe();
    } catch (err) {
      console.error('Realtime error:', err);
    }
  };

  setupRealtime();

  return () => {
    mounted = false;
    if (channel) {
      supabase.removeChannel(channel);
      channel = null;
    }
  };
}, [fetchData]);

  const statCards = [
    {
      label: 'Orders Today',
      value: stats.ordersToday,
      icon: ShoppingBag,
      bg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      valueColor: 'text-blue-700',
      change: stats.ordersToday === 0 ? 'No orders yet today' : `${stats.ordersToday} orders received`,
      onClick: () => navigate('/orders'),
    },
    {
      label: 'Revenue Today',
      value: `SAR ${stats.revenueToday.toLocaleString()}`,
      icon: TrendingUp,
      bg: 'bg-green-50',
      iconColor: 'text-green-600',
      valueColor: 'text-green-700',
      change: stats.revenueToday === 0 ? 'No revenue yet today' : 'Keep it up! 💪',
    },
    {
      label: 'Total Customers',
      value: stats.totalCustomers,
      icon: Users,
      bg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      valueColor: 'text-purple-700',
      change: stats.totalCustomers === 0 ? 'No customers yet' : `${stats.totalCustomers} customers total`,
      onClick: () => navigate('/customers'),
    },
    {
      label: 'Pending Orders',
      value: stats.pendingOrders,
      icon: Clock,
      bg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      valueColor: stats.pendingOrders > 0 ? 'text-orange-700' : 'text-gray-700',
      change: stats.pendingOrders === 0 ? 'All caught up! ✅' : `${stats.pendingOrders} need attention`,
      onClick: () => navigate('/orders'),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-100 rounded mb-4 w-24" />
              <div className="h-8 bg-gray-100 rounded mb-2 w-16" />
              <div className="h-3 bg-gray-100 rounded w-32" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 animate-pulse">
          <div className="h-5 bg-gray-100 rounded w-32 mb-6" />
          {[1, 2, 3].map(i => (
            <div key={i} className="h-14 bg-gray-50 rounded-xl mb-3" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Restaurant Banner */}
      {restaurant && (
        <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-xl">{restaurant.name} 🍽️</p>
              {restaurant.address && (
                <p className="text-white/70 text-sm mt-0.5">📍 {restaurant.address}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-white/80 text-xs">Bot Active</span>
                {lastUpdated && (
                  <span className="text-white/50 text-xs ml-2">
                    Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition"
            >
              <RefreshCw size={18} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, bg, iconColor, valueColor, change, onClick }) => (
          <div
            key={label}
            onClick={onClick}
            className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm transition ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${bg}`}>
                <Icon size={18} className={iconColor} />
              </div>
            </div>
            <p className={`text-2xl font-bold ${valueColor}`}>{value}</p>
            <p className="text-xs text-gray-400 mt-1">{change}</p>
          </div>
        ))}
      </div>

      {/* Pending Orders Alert */}
      {stats.pendingOrders > 0 && (
        <div
          onClick={() => navigate('/orders')}
          className="bg-orange-50 border border-orange-200 rounded-2xl px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-orange-100 transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Clock size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-orange-800">
                {stats.pendingOrders} pending {stats.pendingOrders === 1 ? 'order' : 'orders'} need attention!
              </p>
              <p className="text-orange-600 text-xs mt-0.5">Tap to view and confirm orders</p>
            </div>
          </div>
          <span className="text-orange-400 text-lg">→</span>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-gray-800">Recent Orders</h2>
            {refreshing && <Wifi size={14} className="text-primary animate-pulse" />}
          </div>
          <button
            onClick={() => navigate('/orders')}
            className="text-sm text-primary font-medium hover:underline"
          >
            View all →
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShoppingBag size={28} className="text-gray-200" />
            </div>
            <p className="text-gray-400 font-medium">No orders yet</p>
            <p className="text-gray-300 text-sm mt-1">Orders from your WhatsApp bot will appear here in real-time</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {orders.map((order) => {
              const itemsText = Array.isArray(order.items)
                ? order.items.map(i => `${i.name} x${i.qty}`).join(', ')
                : order.items || '';
              const timeStr = new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const customerDisplay = order.customer_name && order.customer_name !== order.customer_phone
                ? order.customer_name
                : `+${order.customer_phone}`;

              return (
                <div
                  key={order.id}
                  onClick={() => navigate('/orders')}
                  className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                      <span className="text-lg">{statusEmoji[order.status] || '📦'}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 text-sm">#{order.id.slice(0, 6)}</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-gray-600 text-sm">{customerDisplay}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">{itemsText}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-gray-800 text-sm">SAR {order.total}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusStyles[order.status] || 'bg-gray-100 text-gray-600'}`}>
                      {order.status}
                    </span>
                    <span className="text-xs text-gray-300 hidden sm:block">{timeStr}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}